import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Check, X, Loader2, Plus, Trash2, Users, CalendarOff } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const fmt = (d) => format(parseISO(d), "d MMM yyyy");
const initials = (n) => n.split(" ").map((p) => p[0]).slice(0, 2).join("");

export default function ManagerReview() {
  const [summary, setSummary] = useState(null);
  const [pending, setPending] = useState([]);
  const [team, setTeam] = useState([]);
  const [decision, setDecision] = useState(null); // {req, action}

  const loadAll = useCallback(() => {
    api.get("/manager/summary").then((r) => setSummary(r.data));
    api.get("/leave/pending").then((r) => setPending(r.data));
    api.get("/users").then((r) => setTeam(r.data));
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-medium tracking-tight">Manager Review</h1>
        <p className="text-sm mt-1" style={{ color: "#6A736D" }}>Approve requests and monitor team availability.</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard label="Pending" value={summary.pending_count} />
          <SummaryCard label="Off today" value={summary.off_today.length} />
          <SummaryCard label="Team size" value={summary.total_employees} />
          <SummaryCard label="Sick records" value={summary.sick_records} />
        </div>
      )}

      <Tabs defaultValue="pending">
        <TabsList style={{ backgroundColor: "#F4F3ED" }}>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending ({pending.length})</TabsTrigger>
          <TabsTrigger value="balances" data-testid="tab-balances">Team Balances</TabsTrigger>
          <TabsTrigger value="sick" data-testid="tab-sick">Sick Records</TabsTrigger>
          <TabsTrigger value="holidays" data-testid="tab-holidays">Public Holidays</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <PendingList pending={pending} team={team} onDecide={(req, action) => setDecision({ req, action })} />
        </TabsContent>

        <TabsContent value="balances" className="mt-4">
          <TeamBalances team={team} />
        </TabsContent>

        <TabsContent value="sick" className="mt-4">
          <SickRecords team={team} />
        </TabsContent>

        <TabsContent value="holidays" className="mt-4">
          <HolidaysManager />
        </TabsContent>
      </Tabs>

      <DecisionDialog decision={decision} onClose={() => setDecision(null)} onDone={() => { setDecision(null); loadAll(); }} team={team} />
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="bg-white border rounded-lg p-5" style={{ borderColor: "#EAE8E1" }} data-testid={`summary-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <p className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "#6A736D" }}>{label}</p>
      <p className="font-heading text-3xl font-medium mt-2">{value}</p>
    </div>
  );
}

function balanceFor(team, userId) {
  return team.find((t) => t.id === userId);
}

function PendingList({ pending, team, onDecide }) {
  if (pending.length === 0)
    return <div className="bg-white border rounded-lg p-10 text-center text-sm" style={{ borderColor: "#EAE8E1", color: "#6A736D" }}>No pending requests. All caught up!</div>;
  return (
    <div className="space-y-3">
      {pending.map((r) => {
        const bal = balanceFor(team, r.user_id);
        return (
          <div key={r.id} className="bg-white border rounded-lg p-5" style={{ borderColor: "#EAE8E1" }} data-testid={`pending-${r.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="font-heading font-medium">{r.user_name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full border" style={{ backgroundColor: "#EAF0F5", color: "#3F5E7D", borderColor: "#CBDCEA" }}>Holiday</span>
                </div>
                <p className="text-sm mt-1">
                  {fmt(r.start_date)}{r.start_date !== r.end_date ? ` – ${fmt(r.end_date)}` : ""}
                  {r.duration === "half" && <span className="ml-1" style={{ color: "#A35C3E" }}>(½ day)</span>}
                  <span className="font-medium ml-2">· {r.days} day{r.days !== 1 ? "s" : ""}</span>
                </p>
                {r.note && <p className="text-sm mt-1" style={{ color: "#6A736D" }}>“{r.note}”</p>}
                {bal && (
                  <p className="text-xs mt-2" style={{ color: "#6A736D" }}>
                    Balance: <span className="font-medium" style={{ color: "#2C332E" }}>{bal.remaining}</span> of {bal.annual_allowance} days remaining
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`reject-${r.id}`} onClick={() => onDecide(r, "reject")} className="flex items-center gap-1.5 text-sm border rounded-md px-3 py-2 hover:bg-[#FAECEE] transition-colors" style={{ borderColor: "#EAC6CC", color: "#8C3B4A" }}>
                  <X className="w-4 h-4" /> Reject
                </button>
                <button data-testid={`approve-${r.id}`} onClick={() => onDecide(r, "approve")} className="flex items-center gap-1.5 text-sm rounded-md px-3 py-2 text-white transition-colors" style={{ backgroundColor: "#647D6C" }}>
                  <Check className="w-4 h-4" /> Approve
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TeamBalances({ team }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {team.map((t) => {
        const pct = t.annual_allowance ? Math.min(100, (t.used / t.annual_allowance) * 100) : 0;
        return (
          <div key={t.id} className="bg-white border rounded-lg p-5" style={{ borderColor: "#EAE8E1" }} data-testid={`balance-${t.id}`}>
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10 border" style={{ borderColor: "#EAE8E1" }}>
                {t.avatar_url && <AvatarImage src={t.avatar_url} alt={t.name} />}
                <AvatarFallback style={{ backgroundColor: "#647D6C", color: "white" }}>{initials(t.name)}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="font-medium">{t.name}</p>
                <p className="text-xs capitalize" style={{ color: "#6A736D" }}>{t.job_title || t.role}</p>
              </div>
              <span className="text-sm font-medium">{t.remaining}/{t.annual_allowance}</span>
            </div>
            <Progress value={pct} className="h-2 mt-3" />
            <div className="flex items-center justify-between text-xs mt-2" style={{ color: "#6A736D" }}>
              <span>{t.used} taken</span>
              <span className="flex items-center gap-1"><CalendarOff className="w-3 h-3" /> {t.sick_days} sick</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SickRecords({ team }) {
  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    const params = { type: "sick" };
    if (filter !== "all") params.employee_id = filter;
    api.get("/leave", { params }).then((r) => setRecords(r.data));
  }, [filter]);
  return (
    <div className="space-y-4">
      <div className="w-56">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="sick-filter-trigger" style={{ borderColor: "#EAE8E1" }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All employees</SelectItem>
            {team.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`filter-${t.id}`}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white border rounded-lg overflow-hidden" style={{ borderColor: "#EAE8E1" }}>
        <table className="w-full">
          <thead><tr style={{ backgroundColor: "#F9F8F5" }}>
            {["Employee", "Dates", "Days", "Category", "Notes"].map((h) => (
              <th key={h} className="text-xs uppercase tracking-wider font-medium px-4 py-3 text-left" style={{ color: "#6A736D" }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {records.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm" style={{ color: "#6A736D" }}>No sick records.</td></tr>
            ) : records.map((r) => (
              <tr key={r.id} className="border-b" style={{ borderColor: "#EAE8E1" }} data-testid={`sick-record-${r.id}`}>
                <td className="px-4 py-3 text-sm font-medium">{r.user_name}</td>
                <td className="px-4 py-3 text-sm">{fmt(r.start_date)}{r.start_date !== r.end_date ? ` – ${fmt(r.end_date)}` : ""}</td>
                <td className="px-4 py-3 text-sm">{r.days}</td>
                <td className="px-4 py-3 text-sm capitalize">{(r.category || "").replace("_", " ")}</td>
                <td className="px-4 py-3 text-sm" style={{ color: "#6A736D" }}>{r.note || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HolidaysManager() {
  const [holidays, setHolidays] = useState([]);
  const [form, setForm] = useState({ name: "", date: "" });
  const load = useCallback(() => api.get("/holidays").then((r) => setHolidays(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name || !form.date) return;
    try {
      await api.post("/holidays", form);
      toast.success("Public holiday added");
      setForm({ name: "", date: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const del = async (id) => { await api.delete(`/holidays/${id}`); load(); };

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-5 flex items-end gap-3 flex-wrap" style={{ borderColor: "#EAE8E1" }}>
        <div className="flex-1 min-w-[160px]">
          <label className="text-sm font-medium">Name</label>
          <input data-testid="holiday-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Christmas Day" className="mt-1.5 w-full bg-white border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#647D6C]/20" style={{ borderColor: "#EAE8E1" }} />
        </div>
        <div>
          <label className="text-sm font-medium">Date</label>
          <input data-testid="holiday-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 block bg-white border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#647D6C]/20" style={{ borderColor: "#EAE8E1" }} />
        </div>
        <button data-testid="add-holiday-button" onClick={add} className="flex items-center gap-1.5 text-white rounded-md px-4 py-2 font-medium" style={{ backgroundColor: "#647D6C" }}>
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      <div className="bg-white border rounded-lg overflow-hidden" style={{ borderColor: "#EAE8E1" }}>
        {holidays.length === 0 ? (
          <div className="p-10 text-center text-sm" style={{ color: "#6A736D" }}>No public holidays set.</div>
        ) : holidays.map((h) => (
          <div key={h.id} className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "#EAE8E1" }} data-testid={`holiday-${h.id}`}>
            <div className="flex items-center gap-3">
              <Users className="w-4 h-4" style={{ color: "#948A7B" }} />
              <span className="text-sm font-medium">{h.name}</span>
              <span className="text-sm" style={{ color: "#6A736D" }}>{fmt(h.date)}</span>
            </div>
            <button data-testid={`delete-holiday-${h.id}`} onClick={() => del(h.id)} className="text-sm" style={{ color: "#8C3B4A" }}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DecisionDialog({ decision, onClose, onDone }) {
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setComment(""); }, [decision]);
  if (!decision) return null;
  const { req, action } = decision;
  const isApprove = action === "approve";

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/leave/${req.id}/${action}`, { comment });
      toast.success(`Request ${isApprove ? "approved" : "rejected"}`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!decision} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">{isApprove ? "Approve" : "Reject"} request</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <p className="text-sm">{req.user_name} · {fmt(req.start_date)}{req.start_date !== req.end_date ? ` – ${fmt(req.end_date)}` : ""} · {req.days} day{req.days !== 1 ? "s" : ""}</p>
          <div>
            <label className="text-sm font-medium">Comment (optional)</label>
            <textarea data-testid="decision-comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} className="mt-1.5 w-full bg-white border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#647D6C]/20" style={{ borderColor: "#EAE8E1" }} placeholder={isApprove ? "Enjoy your time off!" : "Reason for rejection"} />
          </div>
        </div>
        <DialogFooter>
          <button
            data-testid="confirm-decision-button"
            onClick={submit}
            disabled={saving}
            className="rounded-md px-4 py-2 font-medium text-white flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: isApprove ? "#647D6C" : "#8C3B4A" }}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isApprove ? "Confirm approval" : "Confirm rejection"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
